import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizCodeFreetext from "@/components/storyboard/quiz/QuizCodeFreetext";
import { deriveDefaultLanguage } from "@/components/storyboard/code/codeUtils";
import { useCappedCodeQuiz } from "@/hooks/useCappedCodeQuiz";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { QuizCodeFreetextEntryData } from "@/types/storyboard";

export interface QuizCodeFreetextFlowProps {
  id: string;
  title: string;
  entries: QuizCodeFreetextEntryData[];
  onValidate: (code: string, entryIndex: number) => Promise<ValidationResult>;
}

/**
 * Code-freetext quiz embedded in the shared interactive-element flow. The editor
 * is seeded from the learner's saved submissions; "Code prüfen" runs
 * server-side (LLM) validation, which also persists the code. No localStorage —
 * durable state lives in validation_data, and so do the verdicts the card
 * recaps.
 *
 * Each task grants three tries, tracked per task: the learner can move between
 * tasks freely, and the quiz ends once none of them can be worked on any more.
 */
const QuizCodeFreetextFlow = ({
  id,
  title,
  entries,
  onValidate,
}: QuizCodeFreetextFlowProps) => {
  const language = deriveDefaultLanguage(
    entries.flatMap(
      (entry) => entry.solutions ?? entry.referenceSolutions ?? []
    )
  );

  const {
    submissions,
    refresh,
    statusOverride,
    completedCtaLabel,
    showCloseCta,
  } = useCappedCodeQuiz({
    id,
    prompts: entries.map(
      (entry) => entry.challenge ?? entry.codeChallenge ?? ""
    ),
    language,
  });

  return (
    <QuizInteractiveFlow
      id={id}
      title={title}
      illustrationKind="code_freetext"
      size="wide"
      supportsInProgress
      statusOverride={statusOverride}
      completedCtaLabel={completedCtaLabel}
      showCloseCta={showCloseCta}
      showCompletedBadge={false}
      renderQuiz={() => (
        <QuizCodeFreetext
          entries={entries}
          onValidate={onValidate}
          initialSubmissions={submissions}
          onSubmitted={refresh}
        />
      )}
    />
  );
};

export default QuizCodeFreetextFlow;
